import type { ReactNode } from 'react';

import { faAngleLeft, faGear } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@heroui/react';
import { HEADER_HEIGHT } from '@school-manage/common/constants';
import { useScrollVisibility } from '@school-manage/hooks/useScrollVisibility';
import { useRouter } from '@tanstack/react-router';
import { motion } from 'framer-motion';

interface Props {
  backBtnHandler?: () => void;
  hasBackBtn?: boolean;
  hasSettingBtn?: boolean;
  height?: number;
  leftElement?: ReactNode;
  rightElement?: ReactNode;
  title?: string;
}

export function HeaderBar({
  backBtnHandler = undefined,
  hasBackBtn = false,
  hasSettingBtn = false,
  height = HEADER_HEIGHT,
  leftElement = undefined,
  rightElement = undefined,
  title = undefined,
}: Props) {
  const { history, navigate } = useRouter();
  const { translateY } = useScrollVisibility({
    hasTransform: true,
    hiddenTransform: -HEADER_HEIGHT,
  });

  return (
    <>
      <div className="bg-background fixed top-0 z-50 h-[env(safe-area-inset-top)] w-screen" />
      <motion.header
        className="border-default-200 bg-background fixed top-[env(safe-area-inset-top)] z-40 grid w-full grid-cols-12 items-center border-b px-[16px] transition-[background-color,border-color,transform] duration-300"
        style={{
          height,
          transform: `translateY(${translateY}px)`,
        }}
      >
        <div className="col-span-3 flex">
          {leftElement ||
            (hasBackBtn && (
              <Button
                // NextUI cannot custom size by default
                className="min-w-0 bg-transparent duration-300 active:scale-[0.97]"
                isIconOnly
                onPress={backBtnHandler || (() => history.back())}
                size="sm"
              >
                <FontAwesomeIcon className="text-[24px]" icon={faAngleLeft} />
              </Button>
            ))}
        </div>
        <div className="col-span-6 mx-[16px] flex items-center justify-center truncate text-[16px] font-bold">
          <span className="duration-300">{title}</span>
        </div>
        <div className="col-span-3 ml-auto flex truncate">
          {rightElement ||
            (hasSettingBtn && (
              <Button
                className="m-0 bg-transparent duration-300 active:scale-[0.97]"
                isIconOnly
                onPress={() => navigate({ to: '/setting' })}
                size="sm"
              >
                <FontAwesomeIcon className="text-[24px]" icon={faGear} />
              </Button>
            ))}
        </div>
      </motion.header>
    </>
  );
}
